/**
 * Dashplan — the daily itinerary.
 *
 * One day at a time on a clock-time timeline, with a live now-marker when the
 * shown day is today. Recurring blocks are materialised on open, which is why
 * `materializeDay` fires from an effect keyed on the date.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  CalendarDays,
  Copy,
  LayoutTemplate,
  Repeat,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

import { Timeline } from "@/components/dashplan/timeline";
import type { TimelineEntry } from "@/components/dashplan/block";
import {
  BlockEditor,
  blockDraftFrom,
  emptyDraft,
  type BlockDraft,
} from "@/components/dashplan/block-editor";
import { useNowMinute } from "@/components/dashplan/now-marker";
import { TemplatesDialog } from "@/components/dashplan/templates-dialog";
import { RecurrencesDialog } from "@/components/dashplan/recurrences-dialog";
import { DayStats } from "@/components/dashplan/day-stats";
import {
  todayKey,
  addDays,
  formatDayLabel,
  weekOf,
  formatMinute,
  minuteOfDay,
} from "@/lib/dates";
import { cn } from "@/lib/utils";

export default function DashplanPage() {
  const params = useParams<{ date?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const today = todayKey();
  const date = params.date ?? today;
  const isToday = date === today;

  const entries = useQuery(api.dashplan.day, { date });
  const materializeDay = useMutation(api.dashplan.materializeDay);
  const createEntry = useMutation(api.dashplan.create);
  const updateEntry = useMutation(api.dashplan.update);
  const moveEntry = useMutation(api.dashplan.move);
  const setStatus = useMutation(api.dashplan.setStatus);
  const removeEntry = useMutation(api.dashplan.remove);
  const copyDay = useMutation(api.dashplan.copyDay);
  const clearDay = useMutation(api.dashplan.clearDay);

  const nowMinute = useNowMinute(isToday);

  const [editing, setEditing] = useState<
    | { mode: "create"; draft: BlockDraft }
    | { mode: "edit"; id: Id<"dashplanEntries">; draft: BlockDraft }
    | null
  >(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [recurrencesOpen, setRecurrencesOpen] = useState(false);

  // Materialise recurring blocks whenever a day is opened. Held in a ref so
  // the mutation's changing identity does not retrigger the effect, and
  // guarded per-date so a re-render never fires it twice for the same day.
  const materializeRef = useRef(materializeDay);
  materializeRef.current = materializeDay;
  const materializedDates = useRef(new Set<string>());

  useEffect(() => {
    if (materializedDates.current.has(date)) return;
    materializedDates.current.add(date);

    materializeRef.current({ date }).catch((error) => {
      // Not fatal — the day still renders, just without today's recurrences.
      console.warn("Could not materialise recurring blocks", error);
      materializedDates.current.delete(date);
    });
  }, [date]);

  const week = useMemo(() => weekOf(date), [date]);
  const weekSummary = useQuery(api.dashplan.range, {
    startDate: week[0],
    endDate: week[6],
  });

  const goTo = (nextDate: string) =>
    navigate(nextDate === today ? "/dashplan" : `/dashplan/${nextDate}`);

  const saveDraft = async (draft: BlockDraft) => {
    if (!editing) return;

    if (editing.mode === "create") {
      await createEntry({
        date,
        title: draft.title,
        notes: draft.notes || undefined,
        kind: draft.kind,
        startMinute: draft.startMinute,
        durationMinutes: draft.durationMinutes,
        location: draft.location || undefined,
        linkedTaskId: draft.linkedTaskId ?? undefined,
        linkedGoalId: draft.linkedGoalId ?? undefined,
      });
    } else {
      await updateEntry({
        id: editing.id,
        title: draft.title,
        notes: draft.notes,
        kind: draft.kind,
        startMinute: draft.startMinute,
        durationMinutes: draft.durationMinutes,
        location: draft.location,
        // null clears the link; the mutation treats undefined as "leave alone".
        linkedTaskId: draft.linkedTaskId,
        linkedGoalId: draft.linkedGoalId,
      });
    }
  };

  const toggleStatus = async (entry: TimelineEntry) => {
    await setStatus({
      id: entry._id,
      status: entry.status === "done" ? "planned" : "done",
    });
  };

  const loading = entries === undefined;

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Day header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            aria-label="Previous day"
            onClick={() => goTo(addDays(date, -1))}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label="Next day"
            onClick={() => goTo(addDays(date, 1))}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="min-w-0">
          <h1 className="page-title text-xl font-semibold truncate">
            {formatDayLabel(date, today)}
          </h1>
          <p className="text-xs text-muted-foreground font-mono tabular-nums">
            {date}
            {isToday && ` · now ${formatMinute(nowMinute)}`}
          </p>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {!isToday && (
            <Button variant="ghost" size="sm" onClick={() => goTo(today)}>
              <CalendarDays className="w-4 h-4 mr-1.5" />
              Today
            </Button>
          )}

          <Button
            size="sm"
            data-offline-disable
            onClick={() =>
              setEditing({
                mode: "create",
                // Default a new block to the next half hour on today, or 09:00
                // on any other day where "now" is meaningless.
                draft: emptyDraft(
                  isToday
                    ? Math.min(1410, Math.ceil(minuteOfDay() / 30) * 30)
                    : 9 * 60,
                ),
              })
            }
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add block
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="Day actions"
                data-offline-disable
              >
                <LayoutTemplate className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onSelect={() => setTemplatesOpen(true)}>
                <LayoutTemplate className="w-4 h-4 mr-2" />
                Templates…
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setRecurrencesOpen(true)}>
                <Repeat className="w-4 h-4 mr-2" />
                Repeating blocks…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={async () => {
                  const { copied } = await copyDay({
                    fromDate: addDays(date, -1),
                    toDate: date,
                  });
                  toast({
                    title: copied
                      ? `Copied ${copied} block${copied === 1 ? "" : "s"}`
                      : "Nothing to copy",
                    description: copied
                      ? `From ${addDays(date, -1)}.`
                      : `${addDays(date, -1)} had no blocks.`,
                  });
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy yesterday
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={async () => {
                  const { removed } = await clearDay({ date });
                  toast({
                    title: removed
                      ? `Cleared ${removed} block${removed === 1 ? "" : "s"}`
                      : "Day was already empty",
                  });
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear day
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Week strip */}
      <div className="grid grid-cols-7 gap-1">
        {week.map((dayKey) => {
          const summary = weekSummary?.find((row) => row.date === dayKey);
          const active = dayKey === date;
          return (
            <button
              key={dayKey}
              type="button"
              onClick={() => goTo(dayKey)}
              aria-current={active ? "date" : undefined}
              className={cn(
                "rounded-lg border px-1 py-2 text-center transition-colors",
                active
                  ? "border-primary bg-primary/10"
                  : "border-border hover:bg-muted",
                dayKey === today && !active && "border-primary/40",
              )}
            >
              <div className="text-[10px] uppercase text-muted-foreground">
                {new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(
                  new Date(`${dayKey}T12:00:00`),
                )}
              </div>
              <div className="text-sm font-medium tabular-nums">
                {Number(dayKey.slice(8, 10))}
              </div>
              <div className="text-[10px] text-muted-foreground tabular-nums h-3">
                {summary ? `${summary.done}/${summary.total}` : ""}
              </div>
            </button>
          );
        })}
      </div>

      <DayStats entries={entries ?? []} nowMinute={isToday ? nowMinute : null} />

      {/* Timeline */}
      {loading ? (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            Loading your day…
          </CardContent>
        </Card>
      ) : (
        <Timeline
          entries={entries}
          nowMinute={isToday ? nowMinute : null}
          // `move` already takes an optional duration, so a resize is the
          // same mutation as a drag rather than a second round trip.
          onMove={(id, { startMinute, durationMinutes }) =>
            moveEntry({
              id: id as Id<"dashplanEntries">,
              startMinute,
              durationMinutes,
            }).catch((error) =>
              toast({
                title: "Could not reschedule",
                description: String(error),
                variant: "destructive",
              }),
            )
          }
          onSelect={(entry) =>
            setEditing({
              mode: "edit",
              id: entry._id,
              draft: blockDraftFrom(entry),
            })
          }
          onToggleStatus={toggleStatus}
          onCreateAt={(startMinute) =>
            setEditing({ mode: "create", draft: emptyDraft(startMinute) })
          }
        />
      )}

      {editing && (
        <BlockEditor
          // Keyed so switching blocks remounts the form with fresh state.
          key={editing.mode === "edit" ? editing.id : "create"}
          open
          onOpenChange={(open) => !open && setEditing(null)}
          initial={editing.draft}
          entryId={editing.mode === "edit" ? editing.id : undefined}
          onSave={saveDraft}
          onDelete={
            editing.mode === "edit"
              ? async () => {
                  await removeEntry({ id: editing.id });
                }
              : undefined
          }
        />
      )}

      <TemplatesDialog
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        date={date}
      />
      <RecurrencesDialog
        open={recurrencesOpen}
        onOpenChange={setRecurrencesOpen}
        date={date}
      />
    </div>
  );
}
